import { Grid, Tab, Tabs } from '@mui/material'
import React, { useContext, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardInfoContext, DashboardInfoPayload } from '../../Context/dashboardContext';
import { useInsights } from '../../Context/insightContext';
import styles from './insights.module.css';
import {
    GlobalResponseModel,
    IApplicationInfo,
    IGetAnalysisResultValue,
    ISaveAnalysisResultPayload,
    INewAnalysisResult
} from '../../Models/applicationModels';
import agent from '../../API/apiCalls';
import { UserContext, UserPayload } from '../../Context/userContext';
import { toast } from 'react-toastify';
import InsightPageSwitcher from "./InsighPageSwicher";
import ActionSection from "./ActionSections/ActionSection";
import { LockUnlockHeader } from './components/applicationDetails/lockUnlockHeader/lockUnlockHeader.index';

const InsightsDetails = () => {

    const {
        finalTechnicalOutecomeList,
        setApplicantFullName
    } = useContext(DashboardInfoContext) as DashboardInfoPayload;
    const { setShowLoader } = React.useContext(UserContext) as UserPayload;
    const {
        setDetailsPageEscalations,
        setDetailsPageOpportunityId,
        detailsPageEscalations,
        setAssociatedEscalation,
        setDetailsPageApplicationDetails,
        detailsPageApplicationDetails
    } = useInsights();

    const [currentTab, setCurrentTab] = useState(0)
    const [currentApplicationAnalysisResult, setCurrentApplicationAnalysisResult] = useState<IGetAnalysisResultValue | null>(null);
    const [newAnalysisResult, setNewAnalysisResult] = useState<INewAnalysisResult>({
        finalOutcome: null,
        finalOutcomeId: "",
        rasUrl: "",
        notes: "",
        automatedNotes: null,
        recordingLinks: null
    });

    const [searchParams] = useSearchParams();
    const navigate = useNavigate()

    useEffect(() => {
        (async () => {
            const currentOpportunityId = searchParams.get('opportunityId') ?? "";

            if (currentOpportunityId) {
                try {
                    await GetAndSetAnalysisDataApi(currentOpportunityId);
                } catch (error) {
                    console.error("Error fetching analysis data:", error);
                }
            } else {
                setDetailsPageOpportunityId(null);
                setDetailsPageEscalations(null);
                setAssociatedEscalation([]);
                navigate("/insights");
            }
        })();
    }, [searchParams, navigate]);

    useEffect(() => {
        return () => {
            setDetailsPageOpportunityId(null);
            setDetailsPageEscalations(null);
            setAssociatedEscalation([]);
        };
    }, []);

    useEffect(() => {
        if (!currentApplicationAnalysisResult) return;
        SetDefaultValuesOfAnalysis();
    }, [currentApplicationAnalysisResult, finalTechnicalOutecomeList]);


    const SetDefaultValuesOfAnalysis = () => {
        const defaultFinalOutcome = finalTechnicalOutecomeList.find((v) => v.finalTechnicalOutcomeLabel === currentApplicationAnalysisResult?.finalOutcomeTechnical);
        const newVlue = {
            finalOutcome: defaultFinalOutcome ? defaultFinalOutcome : null,
            finalOutcomeId: currentApplicationAnalysisResult?.finalOutcomeTechnical ?? "",
            rasUrl: currentApplicationAnalysisResult?.rasUrl ?? "",
            notes: currentApplicationAnalysisResult?.notes ?? "",
            automatedNotes: currentApplicationAnalysisResult?.automatedNotes ?? null,
            recordingLinks: currentApplicationAnalysisResult?.recordingLinks ?? null
        }
        setNewAnalysisResult(newVlue)
    }

    const handleLaunchApplication = () => {
        const applicationUrl = detailsPageApplicationDetails?.applicationUrl as string;

        if (!applicationUrl) return;
        window.open(applicationUrl, "_blank");
    }

    const CheckAnyDataChanged = () => {
        const oldAnalysisData = { ...currentApplicationAnalysisResult };
        const finalChangedData = { ...newAnalysisResult };
        const changedFields: string[] = [];

        if (oldAnalysisData.notes !== finalChangedData.notes) {
            changedFields.push("notes");
        }
        if (oldAnalysisData.rasUrl !== finalChangedData.rasUrl) {
            changedFields.push("rasUrl");
        }
        if (oldAnalysisData.finalOutcomeTechnical !== finalChangedData.finalOutcomeId) {
            changedFields.push("finalOutcomeTechnical");
        }
        return changedFields;
    };

    const UpdateAnalysisResultApi = async (changedFields: string[]) => {
        try {
            setShowLoader(true);
            const payload: ISaveAnalysisResultPayload = {
                ibAction: null,
                notes: changedFields.includes("notes") ? newAnalysisResult.notes : null,
                rasUrl: changedFields.includes("rasUrl") ? newAnalysisResult.rasUrl : null,
                finalTechnicalOutcomeUid: changedFields.includes("finalOutcomeTechnical") ? newAnalysisResult.finalOutcomeId : null,
                opportunityUid: currentApplicationAnalysisResult?.opportunityId ?? "",
            };

            const response = await agent.SaveAnalysisResult.post(payload);
            setShowLoader(false);
            if (response?.isAxiosError || !response.flagStatus) {
                toast.error("Something went wrong, Please try again later!");
                return false;
            }
            else if (response.flagStatus) {
                toast.success("Data saved successfully!");
                return true;
            }
            return false;
        }
        catch (err) {
            setShowLoader(false);
            console.error(err);
            toast.error("Something went wrong, Please try again later!");
            return false;
        }
    };

    const SaveAnalysisResult = async () => {
        const changedFields = CheckAnyDataChanged();
        if (changedFields.length === 0) return
        const isSavedSuccessfully = await UpdateAnalysisResultApi(changedFields);

        if (!isSavedSuccessfully) return;

        const currentOpportunityId = currentApplicationAnalysisResult?.opportunityId ?? detailsPageApplicationDetails?.opportunityUid;

        if (typeof currentOpportunityId !== "string" || !currentOpportunityId) return

        await GetAndSetAnalysisDataApi(currentOpportunityId);
    };

    const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
        setCurrentTab(newValue);
    };

    const GetAndSetAnalysisDataApi = async (currentOpportunityId: string) => {
        try {
            setShowLoader(true);
            const res: GlobalResponseModel<IApplicationInfo> = await agent.GetApplicationInfoByOpportunityId.get(currentOpportunityId);

            if (!res.flagStatus || res.isAxiosError || res.error) {
                console.error("Unexpected error in fetching InsightsPage details: ", res.error);
                throw new Error(JSON.stringify(res));
            }

            const { analysis, application, escalations } = res.value;
            setDetailsPageApplicationDetails(application);
            setDetailsPageEscalations(escalations);
            setDetailsPageOpportunityId(currentOpportunityId);
            setAssociatedEscalation(Array.isArray(application?.associatedEscalations) ? (application.associatedEscalations) : []);
            setCurrentApplicationAnalysisResult({
                ...analysis,
                opportunityId: currentOpportunityId
            });
            setApplicantFullName((application.firstName as string ?? "") + " " + (application.lastName as string ?? "Unknown"));
            setShowLoader(false);

        } catch (err) {
            setShowLoader(false);
            console.error("Printing error", err);
        }
    }

    const HandleRefresh = async (isEscalation?: boolean) => {
        try {
            setShowLoader(true);
            if (!detailsPageApplicationDetails?.opportunityUid) {
                console.log("Opportunity ID is not available");
                return;
            }
            if (!isEscalation) {
                await agent.RefreshAutomatedNotes.get(detailsPageApplicationDetails.opportunityUid as string);
            }
            await GetAndSetAnalysisDataApi(detailsPageApplicationDetails.opportunityUid as string);

        } catch (e) {
            console.log(e);
        } finally {
            setShowLoader(false);
        }
    }

    const flagAutomatedNotesPresent = newAnalysisResult.automatedNotes !== null;
    const oppId = detailsPageApplicationDetails?.opportunityUid as string ?? "";
    const tenantTitle = detailsPageApplicationDetails?.tenantTitle as string ?? ""


    return (
        <>
            {detailsPageApplicationDetails !== null &&
                <div className={styles.detailsPageLayout}>
                    <Grid container alignItems={"end"} className={`${styles.headerCtn} ${styles.detailsPageHeader}`}>
                        <Grid item>
                            <Tabs
                                value={currentTab}
                                onChange={handleChangeTab}
                                aria-label="basic tabs example"
                                sx={{
                                    "& .MuiTab-root": {
                                        color: "#000",
                                    },
                                    "& .Mui-selected": {
                                        color: "#3B78CE",
                                    },
                                    "& .MuiTabs-indicator": {
                                        backgroundColor: "#3B78CE",
                                    }
                                }}
                                className={styles.tabCtn}>
                                <Tab className={styles.tabTxt} label={"Application Details"} id='simple-tab-0' />
                                <Tab className={styles.tabTxt} label="Analysis Details" id='simple-tab-1' />
                                <Tab className={styles.tabTxt} label="Escalation" id='simple-tab-1' />
                            </Tabs>
                        </Grid>
                        <ActionSection
                            currentTab={currentTab}
                            flagAutomatedNotesPresent={flagAutomatedNotesPresent}
                            onRefresh={HandleRefresh}
                            onCancel={SetDefaultValuesOfAnalysis}
                            onSave={SaveAnalysisResult}
                            oppId={oppId}
                            tenantTitle={tenantTitle}
                            onLaunchApplication={handleLaunchApplication}
                            piiRemoved={detailsPageApplicationDetails?.isPIIRemoved as boolean ?? false}
                        />
                    </Grid>
                    { currentTab === 0 &&  <LockUnlockHeader currentAppDetails={detailsPageApplicationDetails} /> }
                    <div className={styles.detailsPageContent}>
                        <InsightPageSwitcher
                            currentTab={currentTab}
                            detailsPageApplicationDetails={detailsPageApplicationDetails}
                            setNewAnalysisResult={setNewAnalysisResult}
                            newAnalysisResult={newAnalysisResult}
                            escalationInfo={detailsPageEscalations}
                        />
                    </div>
                </div>
            }
        </>
    )
}

export default InsightsDetails